<script>
import { v1 as uuid } from 'uuid';
import bus from '../../store/bus';

// eslint-disable-next-line max-len
const regex = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;

export default {
  name: 'Youtube',
  props: {
    label: {
      type: String,
      required: true,
    },
    poster: {
      type: String,
      default: null,
    },
    src: {
      type: String,
      required: true,
    },
    errormessage: {
      type: String,
      required: true,
    },
    errorbuttonlabel: {
      type: String,
      required: true,
    }
  },
  inject: ['oneTrustCheck'],
  data() {
    return {
      guid: uuid(),
      //youtube api data
      isActive: false,
      isApiReady: false,
      isPlayerReady: false,
      isReady: false,
      player: null,
      state: -1,
      //template related data
      showPlayer: true,
    };
  },
  mounted() {
    // update youtube api and youtube player states
    this.update();
    
    bus.$on('youtube:close', this.close);
  },
  watch: {
    isYouTubeReady() {
      console.log("Check it youtube is ready and launch update()");
      this.update();
    },

    'oneTrustCheck.isTargetingCookiesActive'(newVal) {
      if (!newVal) { // If it changes to false
        this.close();
      }
    },
  },
  methods: {
    update() {
      if (!document.body.querySelector('#youtube-iframe-api')) {
        console.log("Loading YouTube iframe API script.");

        const script = document.createElement('script');
        script.id = 'youtube-iframe-api';
        script.src = '//www.youtube.com/iframe_api';
        document.body.insertAdjacentElement('beforeend', script);        
        return;
      }

      if (typeof YT === 'undefined' || typeof YT.Player === 'undefined') {
        console.log("YouTube API is not fully loaded.");
        this.showPlayer = false;
        return;
      }

      this.player = new YT.Player(`youtube-${this.guid}`, {
        playerVars: {
          enablejsapi: 1,
          modestbranding: 1,
          rel: 0,
        },
        videoId: this.videoId,
        events: {
          onReady: () => {
            console.log("onReady: Ready.");
            this.isReady = true;
            // The frameborder attribute on the iframe element is obsolete.
            this.$el.querySelector('iframe').removeAttribute('frameborder');
          },
          onStateChange: (e) => this.onPlayerStateChange(e),
        },
      });

      console.log("Player should be visible.")

      this.showPlayer = true;
    },
    loadApi() {
      console.log("Loading API.");
      
      if (this.isApiReady) return;

      if (!document.body.querySelector('#youtube-iframe-api')) {
        console.log("Inserting YouTube iframe API.");
        const script = document.createElement('script');
        script.id = 'youtube-iframe-api';
        script.src = '//www.youtube.com/iframe_api';
        script.async = true;
        script.onload = () => {
          if (typeof YT === 'undefined' || YT.loading) {
            console.log("YT is not fully loaded.");
            return;
          }
          this.isApiReady = true;
          console.log("YT API is ready.");
          this.initPlayer();
        };
        document.body.insertAdjacentElement('beforeend', script);
      }
    },
    initPlayer() {
      console.log("Initializing player.");

      if (this.isPlayerReady) return;
      if (!this.isTargetingCookiesActive) return;
      if (!this.isApiReady) return;
      if (!this.videoId) return;

      this.player = new YT.Player(`youtube-${this.guid}`, {
        playerVars: {
          enablejsapi: 1,
          modestbranding: 1,
          rel: 0,
        },
        videoId: this.videoId,
        events: {
          onReady: () => {
            console.log("Ready.");
            this.isReady = true;
            // The frameborder attribute on the iframe element is obsolete.
            this.$el.querySelector('iframe').removeAttribute('frameborder');
          },
          onStateChange: (e) => this.onPlayerStateChange(e),
        },
      });

      this.isPlayerReady = true;
    },
    onPlayerStateChange(e) {
      this.state = e.data;
      if (this.state === YT.PlayerState.ENDED) this.close(); // Video ended
    },
    play() {
      if (this.state !== YT.PlayerState.PLAYING) {
        this.isActive = true;
        this.player.playVideo();
      }
    },
    close() {
      if (this.state === YT.PlayerState.PLAYING || this.state === YT.PlayerState.BUFFERING || this.state === 0) {
        this.isActive = false;
        this.player.stopVideo();
      }
    },
  },
  computed: {
    isYouTubeReady() {
      return this.$store.state.isYouTubeReady;
    },
    videoId() {
      return this.src.length === 11 ? this.src : this.src.match(regex)[1];
    },
    videoPoster() {
      return !this.poster ? `//img.youtube.com/vi/${this.videoId}/0.jpg` : this.poster;
    },
  },
  created() {
    window.onYouTubeIframeAPIReady = () => this.$store.dispatch('setYouTubeReady');
  },
};
</script>
<template>
  <div>
    <div 
      v-show="!this.showPlayer || !this.oneTrustCheck.isTargetingCookiesActive" 
      class="show-message youtube__cookie-error-message"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 60" style="max-height: 80px"><path d="M10.15,53.91l5.13-2.96-.92,2.84-3.21,1.85c-.91.52-1.9.78-2.89.78s-1.99-.26-2.89-.78c-1.81-1.05-2.89-2.92-2.89-5.01V7.37c0-2.09,1.08-3.97,2.89-5.01,1.81-1.05,3.98-1.05,5.79,0l16.77,9.68-.63,1.94L10.15,4.09c-.59-.34-1.24-.51-1.89-.51s-1.3.17-1.89.51c-1.19.68-1.89,1.91-1.89,3.28v43.25c0,1.37.71,2.59,1.89,3.28,1.19.69,2.6.69,3.79,0ZM48.6,23.99l-13.86-8-.63,1.94,13.49,7.79c1.19.68,1.89,1.91,1.89,3.28s-.71,2.59-1.89,3.28l-22.35,12.9-.92,2.84,24.27-14.01c1.81-1.05,2.89-2.92,2.89-5.01s-1.08-3.97-2.89-5.01ZM35.94.83c-.52-.17-1.09.12-1.26.64l-18.35,56.48c-.17.52.12,1.09.64,1.26.1.03.21.05.31.05.42,0,.81-.27.95-.69L36.59,2.09c.17-.53-.12-1.09-.64-1.26Z"/></svg>
      <p v-text="errormessage"></p>
      
      <div class="btn btn--black">
        <a class="youtube__cookie-error-bt ot-sdk-show-settings" rel="noopener noreferrer" href="#0" v-text="this.errorbuttonlabel"></a>
      </div>
    </div>
    <div
      v-show="this.showPlayer && this.oneTrustCheck.isTargetingCookiesActive"
      class="youtube"
      :class="{ 'is-active': isActive, 'is-ready': isReady }"
      @click="play"
    >
      <button
        class="youtube__button"
        :disabled="!this.isReady"
        ref="button"
        type="button"
        @click="play"
      >
        <span class="sr-only">{{ label }}</span>
      </button>
      <div
        class="youtube__frame"
        :id="'youtube-' + guid"
      />
      <picture class="youtube__poster">
        <img
          :src="videoPoster"
          alt="YouTube video poster"
        >
      </picture>
    </div>
  </div>
</template>
